import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { describeError } from '../describeError';
import { formatDateTime, sourceSystemLabel, TERMINAL_STATUSES } from '../format';
import { payrollHistoryImportGateway as gateway } from '../index';
import { HISTORY_PATH, IMPORT_BASE, NEW_IMPORT_PATH, batchStepPath, stepForBatch } from '../routes';
import { saveDownloadedFile } from '../saveBlob';
import type { BatchDetail, Capabilities } from '../types';
import { ErrorSummary, StatusBadge } from './basics';

export function ResultsStep({ batch, capabilities, announce }: { batch: BatchDetail; capabilities: Capabilities; announce: (message: string) => void }) {
  const [error, setError] = useState<string | null>(null);

  const downloadReport = async () => {
    setError(null);
    try {
      const file = await gateway.downloadErrorReport(batch.id);
      saveDownloadedFile(file);
      announce(`Downloaded ${file.fileName}.`);
    } catch (err) {
      setError(describeError(err, 'Could not download the validation report.'));
    }
  };

  if (!TERMINAL_STATUSES.includes(batch.status)) {
    // Importing is the one non-terminal status whose "current step" IS this screen: no link to itself, just a way
    // to re-check. Any other in-flight status points back to the step it belongs to.
    const target = stepForBatch(batch);
    return (
      <div className="phi-notice">
        <p>{batch.status === 'Importing' ? 'The import is still running.' : "This import isn't finished yet."}</p>
        {target !== 'results' && <Link to={batchStepPath(batch.id, target)}>Continue this import</Link>}
      </div>
    );
  }

  const imported = batch.status === 'Completed' || batch.status === 'CompletedWithWarnings';

  return (
    <div>
      {error && <ErrorSummary message={error} />}
      {batch.status === 'Failed' && (
        <>
          <InlineAlert>{batch.failureReason ?? 'This import failed.'}</InlineAlert>
          {(batch.runsImported ?? 0) === 0 && <p>Nothing was imported. No payroll records were added.</p>}
        </>
      )}
      {batch.status === 'Canceled' && (
        <>
          <p>This import was canceled. Nothing was imported.</p>
          {batch.failureReason && <p className="muted">{batch.failureReason}</p>}
        </>
      )}
      {imported && (
        <p>
          {batch.status === 'CompletedWithWarnings'
            ? 'The import finished with warnings. The records are now available as read-only imported history.'
            : 'The import finished. The records are now available as read-only imported history.'}
        </p>
      )}

      <dl className="phi-facts" aria-label="Import results">
        <dt>Import status</dt><dd><StatusBadge status={batch.status} /></dd>
        <dt>Records imported</dt>
        <dd>
          {batch.runsImported ?? 0} payroll runs, {batch.statementsImported ?? 0} pay statements
        </dd>
        <dt>Skipped as duplicates</dt>
        <dd>
          {batch.runsSkippedAsDuplicate ?? 0} payroll runs, {batch.statementsSkippedAsDuplicate ?? 0} pay statements
        </dd>
        <dt>Warnings</dt><dd>{batch.warningCount}</dd>
        <dt>Errors</dt><dd>{batch.errorCount}</dd>
        <dt>Batch ID</dt><dd>{batch.id}</dd>
        <dt>Imported by</dt><dd>{batch.createdByEmail}</dd>
        <dt>Import date and time</dt><dd>{formatDateTime(batch.importedAtUtc ?? batch.updatedAtUtc)}</dd>
        <dt>Source platform</dt><dd>{sourceSystemLabel(batch.sourceSystem)}</dd>
      </dl>

      <div className="phi-actions">
        {imported && <Link to={HISTORY_PATH}>View imported payroll history</Link>}
        <button type="button" className="phi-link-button" onClick={() => void downloadReport()}>
          Download validation report
        </button>
        {capabilities.canCreate && <Link to={NEW_IMPORT_PATH}>Start a new import</Link>}
        <Link to={IMPORT_BASE}>Back to import history</Link>
      </div>
    </div>
  );
}
